// Drawer displaying execution call logs for Inbound Webhook Workflows (WP-14).
import { useState, useEffect, useId, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  getWebhookWorkflowCallLogs,
  type WebhookWorkflowDoc,
  type WebhookWorkflowCallDoc,
} from '../../lib/webhookWorkflows';

interface WorkflowCallLogDrawerProps {
  open: boolean;
  onClose: () => void;
  workflow: WebhookWorkflowDoc | null;
}

export function WorkflowCallLogDrawer({ open, onClose, workflow }: WorkflowCallLogDrawerProps) {
  const { t } = useI18n();

  const [logs, setLogs] = useState<WebhookWorkflowCallDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(open && !!workflow, panelRef, onClose);

  const fetchLogs = async () => {
    if (!workflow) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getWebhookWorkflowCallLogs(workflow.id, 50);
      setLogs(data);
    } catch (err: any) {
      setError(err.message || t('webhook.logs.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && workflow) {
      fetchLogs();
    } else {
      setLogs([]);
      setExpandedCallId(null);
    }
  }, [open, workflow]);

  if (!open || !workflow) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/50 backdrop-blur-sm transition-opacity">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-xl h-full bg-white shadow-2xl flex flex-col dark:bg-slate-900 overflow-hidden"
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h3 id={titleId} className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>{t('webhook.logs.drawerTitle')}</span>
              <Badge variant="brand">{workflow.name}</Badge>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {t('webhook.logs.drawerSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchLogs}
              disabled={loading}
            >
              {loading ? t('common.loading') : t('common.refresh')}
            </Button>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.close')}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Aggregate Stats */}
        <div className="grid grid-cols-2 gap-2 p-4 bg-slate-50 border-b border-slate-200 dark:bg-slate-900/40 dark:border-slate-800 text-center">
          <div className="p-2 rounded bg-white dark:bg-slate-800 shadow-sm">
            <span className="text-[11px] text-slate-500 block">{t('webhook.logs.totalInboundCalls')}</span>
            <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
              {workflow.stats?.calls || 0}
            </span>
          </div>
          <div className="p-2 rounded bg-white dark:bg-slate-800 shadow-sm">
            <span className="text-[11px] text-slate-500 block">{t('webhook.logs.lastCalled')}</span>
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              {workflow.stats?.lastCalledAt
                ? new Date(workflow.stats.lastCalledAt).toLocaleString()
                : '—'}
            </span>
          </div>
        </div>

        {/* Logs List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {error && (
            <div className="rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          {loading && logs.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              {t('common.loading')}
            </div>
          ) : logs.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              {t('webhook.logs.noLogs')}
            </div>
          ) : (
            logs.map((log) => {
              const isExpanded = expandedCallId === log.id;
              const isOk = log.responseStatus >= 200 && log.responseStatus < 300;

              return (
                <div
                  key={log.id}
                  className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                >
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedCallId(isExpanded ? null : log.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant={isOk ? 'success' : 'danger'}>
                        {log.responseStatus || 'ERR'}
                      </Badge>
                      <span className="text-xs font-mono text-slate-500">
                        {log.ip}
                      </span>
                      <span className="text-xs text-slate-400 font-mono">
                        {log.durationMs}ms
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-slate-400">
                        {new Date(log.at).toLocaleString()}
                      </span>
                      <span className="text-xs text-slate-400">
                        {isExpanded ? '▲' : '▼'}
                      </span>
                    </div>
                  </div>

                  {log.actionsRun && log.actionsRun.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-2">
                      {log.actionsRun.map((a, i) => (
                        <span
                          key={i}
                          className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Expandable inspector */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                      {log.subscriberId && (
                        <div className="text-xs text-slate-500">
                          {t('webhook.logs.subscriber')}:{' '}
                          <code className="text-primary font-mono">{log.subscriberId}</code>
                        </div>
                      )}

                      {log.requestBodySnippet && (
                        <div>
                          <label className="text-[10px] font-semibold uppercase text-slate-400 block mb-1">
                            {t('webhook.logs.payloadSnippet')}
                          </label>
                          <pre className="text-[11px] font-mono bg-slate-950 p-2 rounded text-emerald-400 max-h-36 overflow-auto">
                            {log.requestBodySnippet}
                          </pre>
                        </div>
                      )}

                      {log.error && (
                        <div>
                          <label className="text-[10px] font-semibold uppercase text-rose-500 block mb-1">
                            {t('webhook.logs.errorReason')}
                          </label>
                          <p className="text-xs text-rose-600 bg-rose-50 p-2 rounded dark:bg-rose-950/40 dark:text-rose-300">
                            {log.error}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
